export type KeyboardShortcutRow = {
  action: string;
  mac: string;
  windows: string;
  note?: string;
};

export type KeyboardShortcutSection = {
  title: string;
  rows: KeyboardShortcutRow[];
};

/** In-app + docs source of truth for editor keyboard shortcuts. */
export const KEYBOARD_SHORTCUT_SECTIONS: KeyboardShortcutSection[] = [
  {
    title: 'History',
    rows: [
      { action: 'Undo', mac: '⌘ Z', windows: 'Ctrl Z' },
      { action: 'Redo', mac: '⌘ ⇧ Z', windows: 'Ctrl Shift Z or Ctrl Y' },
    ],
  },
  {
    title: 'File',
    rows: [
      {
        action: 'Open Save & export menu',
        mac: '⌘ S',
        windows: 'Ctrl S',
      },
      {
        action: 'Paste image from clipboard',
        mac: '⌘ V',
        windows: 'Ctrl V',
        note:
          'Draw tab: pastes a copied image if the clipboard has one; otherwise pastes copied layers (after Copy)',
      },
      {
        action: 'Copy selection',
        mac: '⌘ C',
        windows: 'Ctrl C',
        note: 'Layers, text, shapes (not watermark)',
      },
      {
        action: 'Cut selection',
        mac: '⌘ X',
        windows: 'Ctrl X',
      },
    ],
  },
  {
    title: 'Selection',
    rows: [
      { action: 'Select all on canvas', mac: '⌘ A', windows: 'Ctrl A' },
      {
        action: 'Deselect / exit draw tool',
        mac: 'Esc',
        windows: 'Esc',
        note: 'On Draw tab: clears selection and exits the active shape/text tool',
      },
      {
        action: 'Add to selection (toggle)',
        mac: '⇧ Click',
        windows: 'Shift Click',
      },
      {
        action: 'Add / remove from selection',
        mac: '⌘ Click',
        windows: 'Ctrl Click',
      },
      { action: 'Delete selection', mac: 'Delete or ⌫', windows: 'Delete or Backspace' },
      {
        action: 'Duplicate selection',
        mac: '⌘ D',
        windows: 'Ctrl D',
        note: 'Not for watermark-only selection',
      },
    ],
  },
  {
    title: 'Group & lock',
    rows: [
      {
        action: 'Group selection',
        mac: '⌘ G',
        windows: 'Ctrl G',
        note: '2+ layers (Figma / Photoshop)',
      },
      {
        action: 'Ungroup selection',
        mac: '⌘ ⇧ G',
        windows: 'Ctrl Shift G',
      },
      {
        action: 'Lock / unlock selection',
        mac: '⌘ ⇧ L',
        windows: 'Ctrl Shift L',
        note: 'Toggles like Figma; locks all or unlocks locked items in selection',
      },
    ],
  },
  {
    title: 'Align & distribute',
    rows: [
      {
        action: 'Align left',
        mac: '⌥ A',
        windows: 'Alt A',
        note: 'Figma-style; 2+ selected layers',
      },
      { action: 'Align horizontal center', mac: '⌥ H', windows: 'Alt H' },
      { action: 'Align right', mac: '⌥ D', windows: 'Alt D' },
      { action: 'Align top', mac: '⌥ W', windows: 'Alt W' },
      { action: 'Align vertical middle', mac: '⌥ V', windows: 'Alt V' },
      { action: 'Align bottom', mac: '⌥ S', windows: 'Alt S' },
      {
        action: 'Distribute horizontal spacing',
        mac: '⌃ ⌥ H',
        windows: 'Ctrl Alt H',
        note: '3+ layers',
      },
      {
        action: 'Distribute vertical spacing',
        mac: '⌃ ⌥ V',
        windows: 'Ctrl Alt V',
        note: '3+ layers',
      },
    ],
  },
  {
    title: 'Layer order',
    rows: [
      { action: 'Bring forward', mac: '⌘ ]', windows: 'Ctrl ]' },
      { action: 'Send backward', mac: '⌘ [', windows: 'Ctrl [' },
      { action: 'Bring to front', mac: '⌘ ⇧ ]', windows: 'Ctrl Shift ]' },
      { action: 'Send to back', mac: '⌘ ⇧ [', windows: 'Ctrl Shift [' },
    ],
  },
  {
    title: 'Move selection',
    rows: [
      { action: 'Nudge 1px', mac: 'Arrow keys', windows: 'Arrow keys' },
      { action: 'Nudge 10px', mac: '⇧ Arrow keys', windows: 'Shift Arrow keys' },
    ],
  },
  {
    title: 'Canvas view',
    rows: [
      {
        action: 'Select tool',
        mac: 'V',
        windows: 'V',
        note: 'Bottom toolbar — move and resize layers',
      },
      {
        action: 'Pan tool',
        mac: 'H',
        windows: 'H',
        note: 'Bottom toolbar — drag canvas to pan; Esc returns to Select',
      },
      {
        action: 'Pan (temporary)',
        mac: 'Hold Space + drag',
        windows: 'Hold Space + drag',
        note: 'Select mode only; focus the canvas first',
      },
      {
        action: 'Show original image',
        mac: 'Hold O',
        windows: 'Hold O',
        note: 'Compare before/after; release to hide',
      },
    ],
  },
  {
    title: 'Zoom',
    rows: [
      { action: 'Zoom in', mac: '⌘ +', windows: 'Ctrl +' },
      { action: 'Zoom out', mac: '⌘ −', windows: 'Ctrl −' },
      { action: 'Fit canvas', mac: '⌘ 0', windows: 'Ctrl 0' },
      { action: 'Actual size (100%)', mac: '⌘ 1', windows: 'Ctrl 1' },
    ],
  },
];

export const KEYBOARD_SHORTCUTS_FOOTNOTES = [
  'Shortcuts are disabled while you type in sidebar fields or edit text on the canvas.',
  'Shortcuts are disabled while a modal is open (crop, confirmations, graphics dialog, etc.).',
  'Undo is disabled when the image failed to load.',
  'Zoom and pan shortcuts are disabled during crop and when zooming is turned off in config.',
  'Layer shortcuts apply to the current selection (all selected items move together).',
  'Align shortcuts need 2+ layers; distribute needs 3+. Group needs 2+ ungrouped layers.',
  'Align uses Alt (⌥); distribute uses Ctrl+Alt (⌃⌥ on Mac), matching Figma.',
];
